import { Box } from '@atgfr8/atg-ui-tailwind';
import { CheckCircleOutlineIcon } from '@atgfr8/icons/CheckCircleOutline';
import { InformationOutlineIcon } from '@atgfr8/icons/InformationOutline';
import { PlusCircleOutlineIcon } from '@atgfr8/icons/PlusCircleOutline';
import Button from '@components/Button/Button';
import Popover from '@components/Popover/Popover';
import { LtlQuoteViewModel } from '@store/orders/types';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { formatAmount } from '@utils/utils';
import { Show } from 'solid-js';

import { ViewCostDetailsPopover } from '../../ViewCostDetailsPopover';
import { hasInsurance } from './insurance';
import {
  getAgentTotal,
  getCustomerTotal,
  getInsuranceAndFees,
  getMarginPercent,
  hasCustomerPricing,
} from './pricing';
import {
  QuoteDetailsActions,
  QuoteDetailsInsurance,
  QuoteDetailsLabels,
} from './types';

const CostRow = (props: {
  label: string;
  value: string;
  bold?: boolean;
  indented?: boolean;
  class?: string;
  dataTestId?: string;
}) => (
  <Box class={`flex justify-between gap-2 ${props.class ?? ''}`}>
    <span
      classList={{
        'text-sm': true,
        'font-bold': Boolean(props.bold),
        'ml-5 text-[#666666]': Boolean(props.indented),
      }}
    >
      {props.label}
    </span>
    <span
      classList={{
        'text-right text-wrap': true,
        'text-base': Boolean(props.bold),
        'text-sm text-[#666666]': Boolean(props.indented),
      }}
      data-testid={props.dataTestId}
    >
      {props.value}
    </span>
  </Box>
);

/**
 * Cost card. Rendered once for the carrier-only figures and, when the quote
 * carries insurance, a second time with `isInsuranceCard` for the w/ insurance figures.
 */
export const QuoteCostDetails = (props: {
  quote: LtlQuoteViewModel;
  customerId?: number;
  insurance?: QuoteDetailsInsurance;
  isInsuranceCard: boolean;
  showActions: boolean;
  actions?: QuoteDetailsActions;
  labels: QuoteDetailsLabels;
}) => {
  const insuranceForTotals = () =>
    props.isInsuranceCard ? props.insurance : undefined;
  const showCustomer = () => hasCustomerPricing(props.quote, props.customerId);
  const canAddInsurance = () => Boolean(props.customerId);

  return (
    <Box class="flex flex-col justify-between h-full w-full p-0.5">
      <Box class="flex flex-col gap-1">
        <Box class="flex items-center justify-between gap-2 mb-2">
          <Popover
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            classes="p-0!"
            eleName={
              <span
                class="inline-flex items-center text-[#307eac] text-sm sm:text-base"
                data-testid={`quoteCostDetails-costDetails-quote-${props.quote.id}`}
              >
                <InformationOutlineIcon class="mr-1" />
                <span class="text-wrap">
                  {props.isInsuranceCard ? 'w/ Insurance' : 'Cost Details'}
                </span>
              </span>
            }
          >
            <ViewCostDetailsPopover
              quote={props.quote}
              insuranceQuote={insuranceForTotals()}
            />
          </Popover>
          <Show when={!props.isInsuranceCard && showCustomer()}>
            <span class="text-xs sm:text-sm text-[#222222] text-right text-wrap">
              Margin: {getMarginPercent(props.quote)}%
            </span>
          </Show>
        </Box>

        <CostRow
          label="Agent"
          bold
          value={formatAmount(getAgentTotal(props.quote, insuranceForTotals()))}
          dataTestId={`quoteCostDetails-carrierTotal-quote-${props.quote.id}`}
        />
        <CostRow
          label="Carrier Rate"
          indented
          value={formatAmount(props.quote.carrierTotal)}
        />
        <Show when={props.isInsuranceCard}>
          <CostRow
            label="Insurance Rate"
            indented
            value={formatAmount(props.insurance?.premium ?? 0)}
          />
        </Show>

        <Show when={showCustomer()}>
          <CostRow
            label="Customer"
            bold
            class="mt-4"
            value={formatAmount(
              getCustomerTotal(props.quote, insuranceForTotals()),
            )}
            dataTestId={`quoteCostDetails-customerTotal-quote-${props.quote.id}`}
          />
          <Show when={props.isInsuranceCard}>
            <CostRow
              label="Insurance & Fees"
              indented
              value={formatAmount(getInsuranceAndFees(props.insurance))}
            />
          </Show>
        </Show>
      </Box>

      <Show when={props.showActions}>
        <Box class="flex w-full mt-4 gap-2 flex-wrap">
          <Show when={!props.isInsuranceCard}>
            <Button
              variant="contained"
              startIcon={<CheckCircleOutlineIcon size={20} />}
              class="bg-[#248670]! min-w-0 flex-1 whitespace-normal xl:whitespace-nowrap"
              label={props.labels.select}
              onClick={() => void props.actions?.onSelectQuote?.(props.quote)}
              dataTestId={formatDataTestId(props.labels.select, 'button')}
            />
          </Show>
          <Show when={!props.isInsuranceCard && !hasInsurance(props.insurance)}>
            <Button
              variant="contained"
              startIcon={<PlusCircleOutlineIcon size={20} />}
              class="bg-[#066da1]! min-w-0 flex-1 whitespace-normal xl:whitespace-nowrap"
              label={props.labels.addInsurance}
              disabled={!canAddInsurance()}
              onClick={() => props.actions?.onAddInsurance?.(props.quote)}
              dataTestId={formatDataTestId(props.labels.addInsurance, 'button')}
            />
          </Show>
          <Show when={props.isInsuranceCard}>
            <Button
              variant="contained"
              startIcon={<CheckCircleOutlineIcon size={20} />}
              class="bg-[#248670]! w-full whitespace-normal xl:whitespace-nowrap"
              label={props.labels.selectInsurance}
              onClick={() =>
                props.actions?.onSelectInsuranceQuote?.(props.quote)
              }
              dataTestId={formatDataTestId(
                props.labels.selectInsurance,
                'button',
              )}
            />
          </Show>
        </Box>
      </Show>
    </Box>
  );
};
